/**
 * Messaggi fissati in alto e messaggi importanti (stella) della chat aperta.
 *
 * L'azione vera la fa WAHA sul telefono (pin/unpin/star). Il CRM la ricorda nella
 * tabella whatsapp_message_marks (migration 20260926b) per mostrare la barra in alto e
 * la stellina. Se la tabella non c'e' ancora (migration non applicata) si lavora solo
 * in memoria: l'azione su WhatsApp riesce lo stesso, si perde solo il ricordo.
 */

import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { getWahaService } from '@/lib/services/wahaApiService';

const PIN_SECONDS = 7 * 24 * 3600; // 7 giorni, come la durata predefinita di WhatsApp

interface ChatMarks {
  pinnedId: string | null;
  starred: string[];
}

interface MarksContext {
  configId: string;
  organizationId: string;
  chatJid: string;
}

interface MessageMarksState {
  key: string | null;
  ctx: MarksContext | null;
  marks: ChatMarks;
  load: (ctx: MarksContext) => Promise<void>;
  /** Svuota fissati e stelle (cambio chat o numero, o chat senza questi segni come i numeri Ufficiali). */
  reset: () => void;
  pin: (messageId: string) => Promise<void>;
  unpin: () => Promise<void>;
  setStar: (messageId: string, star: boolean) => Promise<void>;
}

const EMPTY: ChatMarks = { pinnedId: null, starred: [] };
export const keyOf = (c: { configId: string; chatJid: string }) => `${c.configId}|${c.chatJid}`;

async function persistMark(ctx: MarksContext, messageId: string, kind: 'pin' | 'star', on: boolean) {
  try {
    if (on) {
      const row = {
        organization_id: ctx.organizationId,
        config_id: ctx.configId,
        chat_jid: ctx.chatJid,
        message_id: messageId,
        kind,
        pinned_until: kind === 'pin' ? new Date(Date.now() + PIN_SECONDS * 1000).toISOString() : null,
      };
      if (kind === 'pin') {
        // Un solo messaggio fissato per chat: tolgo il precedente.
        await supabase.from('whatsapp_message_marks').delete()
          .eq('config_id', ctx.configId).eq('chat_jid', ctx.chatJid).eq('kind', 'pin');
      }
      const { error } = await supabase.from('whatsapp_message_marks')
        .upsert(row, { onConflict: 'config_id,message_id,kind' });
      if (error) throw error;
    } else {
      const { error } = await supabase.from('whatsapp_message_marks').delete()
        .eq('config_id', ctx.configId).eq('message_id', messageId).eq('kind', kind);
      if (error) throw error;
    }
  } catch (err) {
    // Tabella assente o permessi: l'azione su WhatsApp e' gia' fatta, resta in memoria.
    console.warn('[messageMarks] salvataggio non riuscito:', err);
  }
}

export const useMessageMarksStore = create<MessageMarksState>((set, get) => ({
  key: null,
  ctx: null,
  marks: EMPTY,

  load: async (ctx) => {
    const key = keyOf(ctx);
    set({ key, ctx, marks: EMPTY });
    try {
      const { data, error } = await supabase
        .from('whatsapp_message_marks')
        .select('message_id, kind, pinned_until')
        .eq('config_id', ctx.configId)
        .eq('chat_jid', ctx.chatJid);
      if (error || !data || get().key !== key) return;
      const now = Date.now();
      const pin = data.find((r) => r.kind === 'pin' && (!r.pinned_until || new Date(r.pinned_until).getTime() > now));
      set({
        marks: {
          pinnedId: pin?.message_id ?? null,
          starred: data.filter((r) => r.kind === 'star').map((r) => r.message_id),
        },
      });
    } catch {
      // tabella non ancora creata: nessun segno salvato
    }
  },

  reset: () => set({ key: null, ctx: null, marks: EMPTY }),

  // Le risposte arrivate quando la chat non e' piu' quella aperta non toccano i segni
  // della chat nuova (il salvataggio nel DB resta sulla chat giusta, quella dell'azione).
  pin: async (messageId) => {
    const { ctx, key } = get();
    if (!ctx) return;
    await getWahaService().pinMessage(ctx.chatJid, messageId, PIN_SECONDS);
    if (get().key === key) set((s) => ({ marks: { ...s.marks, pinnedId: messageId } }));
    await persistMark(ctx, messageId, 'pin', true);
  },

  unpin: async () => {
    const { ctx, marks, key } = get();
    if (!ctx || !marks.pinnedId) return;
    const id = marks.pinnedId;
    await getWahaService().unpinMessage(ctx.chatJid, id);
    if (get().key === key) set((s) => ({ marks: { ...s.marks, pinnedId: null } }));
    await persistMark(ctx, id, 'pin', false);
  },

  setStar: async (messageId, star) => {
    const { ctx, key } = get();
    if (!ctx) return;
    await getWahaService().starMessage(ctx.chatJid, messageId, star);
    if (get().key === key) set((s) => ({
      marks: {
        ...s.marks,
        starred: star
          ? Array.from(new Set([...s.marks.starred, messageId]))
          : s.marks.starred.filter((id) => id !== messageId),
      },
    }));
    await persistMark(ctx, messageId, 'star', star);
  },
}));
